import classNames from 'classnames'
import { ReactElement, useId } from 'react'
import type { IconWeight } from '@phosphor-icons/react'

type AIBadgeVariant = `icon` | `badge` | `full`

/**
 * Muted "AI Disabled" pill for the inbox conversation list. Renders when a
 * thread has AI activity but the agent has been manually toggled off — the
 * sparkle is greyscaled and the text spells out the disabled state so the user
 * can see at a glance that no new AI replies will fire.
 */
export function AIBadgePillDisabled({ className }: { className?: string }): ReactElement {
  const id = useId()
  const sparkleId = `ai-disabled-sparkle-${id}`
  return (
    <span
      role="img"
      aria-label="AI Disabled"
      className={classNames(
        `inline-flex items-center gap-1 rounded border border-default bg-surface-subtle px-1.5 py-[3px] text-[10.5px] font-medium leading-none text-subtle`,
        className,
      )}
    >
      <span className="inline-flex opacity-60 grayscale">
        <AISparkle className="h-2.5 w-2.5" gradientId={sparkleId} />
      </span>
      AI Disabled
    </span>
  )
}

/**
 * Compact "✦ AI" pill — sparkle + AI text inside a gradient-stroked rounded
 * rectangle. Used in the inbox conversation list to flag threads where AI has
 * sent a message.
 */
export function AIBadgePill({ className }: { className?: string }): ReactElement {
  const id = useId()
  const strokeId = `ai-pill-stroke-${id}`
  const sparkleId = `ai-pill-sparkle-${id}`
  return (
    <svg
      width="39"
      height="20"
      viewBox="0 0 39 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="AI"
      role="img"
    >
      <rect x="0.5" y="0.5" width="38" height="19" rx="3.5" stroke={`url(#${strokeId})`} strokeOpacity="0.2" />
      <g opacity="0.8">
        <path
          d="M12 6C12 6 12.5327 7.82564 13.3535 8.64648C14.1744 9.46731 16 10 16 10C16 10 14.1745 10.5328 13.3535 11.3535C12.5388 12.1682 12.0079 13.9731 12 14C11.9947 13.9819 11.4632 12.1703 10.6465 11.3535C9.83086 10.5379 8.02295 10.0067 8 10C8.02883 9.99155 9.83222 9.46075 10.6465 8.64648C11.4672 7.82551 12 6 12 6Z"
          fill={`url(#${sparkleId})`}
        />
      </g>
      <path
        d="M22.0727 14H20.3382L22.981 6.3445H25.0669L27.7059 14H25.9715L24.0538 8.0939H23.994L22.0727 14ZM21.9643 10.9909H26.0612V12.2543H21.9643V10.9909ZM30.2459 6.3445V14H28.6273V6.3445H30.2459Z"
        fill="#1D4ED8"
      />
      <defs>
        <linearGradient id={strokeId} x1="32.0911" y1="0" x2="21.4007" y2="27.2623" gradientUnits="userSpaceOnUse">
          <stop stopColor="#67C3FF" />
          <stop offset="0.600962" stopColor="#2A40F5" />
        </linearGradient>
        <linearGradient id={sparkleId} x1="14.5828" y1="6" x2="8.51181" y2="13.9395" gradientUnits="userSpaceOnUse">
          <stop stopColor="#67C3FF" />
          <stop offset="0.600962" stopColor="#2A40F5" />
        </linearGradient>
      </defs>
    </svg>
  )
}

/** The AI sparkle in the brand gradient, or in the text color (`currentColor`) without a `gradientId`. */
export function AISparkle({
  className,
  gradientId,
  size = 10,
}: {
  className?: string
  gradientId?: string
  size?: number | string
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 10 10"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity={gradientId ? 0.8 : 1}>
        <path
          d="M5 1C5 1 5.53267 2.82564 6.35352 3.64648C7.17436 4.46732 9 5 9 5C9 5 7.17436 5.53267 6.35352 6.35352C5.53267 7.17436 5 9 5 9C5 9 4.46732 7.17436 3.64648 6.35352C2.82564 5.53267 1 5 1 5C1 5 2.82564 4.46732 3.64648 3.64648C4.46732 2.82564 5 1 5 1Z"
          fill={gradientId ? `url(#${gradientId})` : `currentColor`}
        />
      </g>
      {gradientId && (
        <defs>
          <linearGradient id={gradientId} x1="7.58278" y1="1" x2="1.51181" y2="8.93951" gradientUnits="userSpaceOnUse">
            <stop stopColor="#67C3FF" />
            <stop offset="0.600962" stopColor="#2A40F5" />
          </linearGradient>
        </defs>
      )}
    </svg>
  )
}

/**
 * The AI sparkle where a phosphor icon is expected (the settings rail, for AI Agent): it takes
 * `size` like one, draws in the text color like one rather than the brand gradient, and ignores
 * `weight`, which a filled mark has no use for.
 */
export function AISparkleIcon({
  size,
  className,
}: {
  size?: number | string
  weight?: IconWeight
  className?: string
}) {
  return <AISparkle size={size} className={className} />
}

function AIFull({ className, gradientId }: { className?: string; gradientId: string }): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.53516 3.79883H3C2.00589 3.79883 1.19922 4.6055 1.19922 5.59961V13.5996C1.19943 14.5935 2.00602 15.3994 3 15.3994H11C11.9938 15.3992 12.7996 14.5934 12.7998 13.5996V7.06445H14V13.5996C13.9998 15.2562 12.6565 16.5994 11 16.5996H3C1.34328 16.5996 0.000212869 15.2563 0 13.5996V5.59961C0 3.94276 1.34315 2.59961 3 2.59961H9.53516V3.79883ZM8.86426 12.8008H7.47656L7.02344 11.4043H4.81152L4.35742 12.8008H2.96973L5.08398 6.67676H6.75293L8.86426 12.8008ZM10.8965 12.8008H9.60156V6.67676H10.8965V12.8008ZM5.14062 10.3936H6.69531L5.94238 8.07617H5.89453L5.14062 10.3936ZM13.6934 0C13.6934 0 14.0857 1.34532 14.6904 1.9502C15.2908 2.55058 16.6211 2.94155 16.6406 2.94727C16.6238 2.9522 15.2914 3.34334 14.6904 3.94434C14.0894 4.54533 13.6983 5.87767 13.6934 5.89453C13.6876 5.87502 13.2967 4.54472 12.6963 3.94434C12.0914 3.33956 10.7461 2.94727 10.7461 2.94727C10.7531 2.94522 12.093 2.55344 12.6963 1.9502C13.2995 1.34695 13.6913 0.00701605 13.6934 0Z"
        fill={`url(#${gradientId})`}
      />
      <defs>
        <linearGradient id={gradientId} x1="13.6927" y1="0" x2="1.1039" y2="16.5041" gradientUnits="userSpaceOnUse">
          <stop stopColor="#67C3FF" />
          <stop offset="0.600962" stopColor="#2A40F5" />
        </linearGradient>
      </defs>
    </svg>
  )
}

/**
 * AI Badge component with 3 variants:
 * - `icon`: Small sparkle icon only (conversation list)
 * - `badge`: Sparkle + "AI" text in a blue-bordered pill (agent status)
 * - `full`: Large AI text + sparkle SVG (inbox header, message cards)
 */
export function AIBadgeIcon({
  className,
  variant = `full`,
}: {
  className?: string
  variant?: AIBadgeVariant
}): ReactElement {
  const id = useId()
  const gradientId = `ai-gradient-${id}`

  if (variant === `icon`) {
    return (
      <span
        className={classNames(
          `flex shrink-0 items-center justify-center rounded border border-ai-accent-light p-[3.5px]`,
          className,
        )}
      >
        <AISparkle gradientId={gradientId} />
      </span>
    )
  }

  if (variant === `badge`) {
    return (
      <span
        className={classNames(
          `flex shrink-0 items-center justify-center gap-0.5 rounded border border-ai-accent-light px-2 py-[5px]`,
          className,
        )}
      >
        <AISparkle gradientId={gradientId} />
        <span className="text-[10.5px] font-bold leading-none text-blue-700">AI</span>
      </span>
    )
  }

  return <AIFull className={className} gradientId={gradientId} />
}
